import { createRng } from "./rng.ts";

export type Biome =
  | "plains"
  | "forest"
  | "hills"
  | "foothills"
  | "mountains"
  | "marsh"
  | "coast"
  | "river"
  | "water";

export type SettlementKind = "seat" | "bannerman" | "political" | "none";

/** Phase 4.5 map triad — non-player political keeps (not vassal banners). */
export type PoliticalSeatKind = "liege" | "peer" | "foe";

export type PoliticalSeat = {
  key: string;
  kind: PoliticalSeatKind;
  q: number;
  r: number;
  name: string;
};

/** Rare strategic resource deposits on terrain (claim → production bonus). */
export type ResourceFlag = "iron" | "gold" | "horses";

export type HexCell = {
  q: number;
  r: number;
  biome: Biome;
  settlement: SettlementKind;
  name?: string;
  /** Phase 4.5: liege / peer / foe when settlement === "political". */
  seatRole?: PoliticalSeatKind;
  /** Keys of neighboring cells connected by a route stub. */
  routes: string[];
  /** Optional rare strategic deposit (iron@mtn, gold@hills, horses@plains). */
  resourceFlag?: ResourceFlag;
  /** Summer-only thaw corridor through mountains (not settleable). */
  mountainPass?: boolean;
};

export interface HexMap {
  seed: string;
  radius: number;
  cells: Map<string, HexCell>;
  seatKey: string;
  bannermanKeys: string[];
  /** Exactly one liege, one peer, one foe when the board has room (play radius). */
  politicalSeats: PoliticalSeat[];
  /** Convenience: liege keep key, or null if not placed. */
  liegeKey: string | null;
}

export function cellKey(q: number, r: number): string {
  return `${q},${r}`;
}

export function parseKey(key: string): { q: number; r: number } {
  const [qs, rs] = key.split(",");
  return { q: Number(qs), r: Number(rs) };
}

/** Axial hex distance. */
export function hexDistance(aq: number, ar: number, bq: number, br: number): number {
  const dq = aq - bq;
  const dr = ar - br;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

const AXIAL_DIRS: readonly [number, number][] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export function neighbors(q: number, r: number): [number, number][] {
  return AXIAL_DIRS.map(([dq, dr]) => [q + dq, r + dr]);
}

/** Land biomes that can host settlements / routes (not open water or river). */
export function isDryLand(biome: Biome): boolean {
  return biome !== "water" && biome !== "river";
}

/** Settlements need dry land that is not a seasonal mountain-pass corridor. */
export function canSettleOn(cell: HexCell): boolean {
  return isDryLand(cell.biome) && !cell.mountainPass;
}

/**
 * Rare strategic deposits after biomes are painted.
 * Iron → mountains only; gold → hills only; horses → plains only.
 * Iron + gold are fairly rare; horses somewhat rare.
 */
export function placeResourceFlags(
  cells: Map<string, HexCell>,
  rng: () => number,
): void {
  // Clear any prior flags (idempotent for tests / regen)
  for (const c of cells.values()) {
    if (c.resourceFlag) delete c.resourceFlag;
  }

  const byBiome = (b: Biome) =>
    [...cells.values()].filter((c) => c.biome === b && c.settlement === "none");

  const mountains = byBiome("mountains");
  const hills = byBiome("hills");
  const plains = byBiome("plains");

  // Shuffle helpers keep placement seed-stable via rng
  const shuffle = <T,>(arr: T[]): T[] => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const t = a[i]!;
      a[i] = a[j]!;
      a[j] = t;
    }
    return a;
  };

  // Fairly rare iron on mountains (~12–18% of mountain tiles, min 1 when any)
  const ironPool = shuffle(mountains);
  const ironCount =
    ironPool.length === 0
      ? 0
      : Math.max(1, Math.min(ironPool.length, Math.round(ironPool.length * (0.12 + rng() * 0.06))));
  for (let i = 0; i < ironCount; i++) ironPool[i]!.resourceFlag = "iron";

  // Fairly rare gold on hills (~10–16%)
  const goldPool = shuffle(hills);
  const goldCount =
    goldPool.length === 0
      ? 0
      : Math.max(1, Math.min(goldPool.length, Math.round(goldPool.length * (0.1 + rng() * 0.06))));
  for (let i = 0; i < goldCount; i++) goldPool[i]!.resourceFlag = "gold";

  // Somewhat rare horses on plains (~6–10%)
  const horsePool = shuffle(plains);
  const horseCount =
    horsePool.length === 0
      ? 0
      : Math.max(
          0,
          Math.min(horsePool.length, Math.round(horsePool.length * (0.06 + rng() * 0.04))),
        );
  // Guarantee at least one horse deposit on boards with enough plains
  const horseTarget =
    horsePool.length >= 8 ? Math.max(1, horseCount) : horseCount;
  for (let i = 0; i < horseTarget; i++) horsePool[i]!.resourceFlag = "horses";
}

/**
 * Multi-pass biome generation:
 * 1) base land + edge water/coast
 * 2) mountains as singular peaks or short ranges
 * 3) foothills more frequent next to mountains
 * 4) winding rivers that always terminate in water
 */
function paintBiomes(
  cells: Map<string, HexCell>,
  rng: () => number,
  radius: number,
): void {
  // Pass 1 — base fill (no mountains/rivers/foothills yet)
  for (const c of cells.values()) {
    const dist = hexDistance(c.q, c.r, 0, 0);
    const edge = dist / Math.max(1, radius);
    const n = rng();
    // P3.8: water-seed incidence −50% vs P3.7 (0.465→0.232); expand pass still fattens bodies
    if (edge > 0.9 && n < 0.232) c.biome = "water";
    else if (edge > 0.72 && n < 0.38) c.biome = "coast";
    else if (n < 0.22) c.biome = "hills";
    else if (n < 0.48) c.biome = "forest";
    else if (n < 0.58) c.biome = "marsh";
    else c.biome = "plains";
  }

  // P3.7 Pass 1b — grow water bodies ~+35% area (fewer seeds, fatter lakes/seas)
  {
    const waterKeys = [...cells.values()]
      .filter((c) => c.biome === "water")
      .map((c) => cellKey(c.q, c.r));
    const growBudget = Math.max(0, Math.floor(waterKeys.length * 0.35));
    let grown = 0;
    for (let i = waterKeys.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const t = waterKeys[i]!;
      waterKeys[i] = waterKeys[j]!;
      waterKeys[j] = t;
    }
    let wi = 0;
    while (grown < growBudget && waterKeys.length > 0 && wi < waterKeys.length * 8) {
      const sk = waterKeys[wi % waterKeys.length]!;
      wi++;
      const start = cells.get(sk);
      if (!start || start.biome !== "water") continue;
      const neigh = neighbors(start.q, start.r)
        .map(([nq, nr]) => cells.get(cellKey(nq, nr)))
        .filter((n): n is HexCell => !!n && n.biome !== "water" && n.settlement === "none");
      if (neigh.length === 0) continue;
      const pick = neigh[Math.floor(rng() * neigh.length)]!;
      pick.biome = "water";
      waterKeys.push(cellKey(pick.q, pick.r));
      grown++;
    }
    for (const c of cells.values()) {
      if (c.biome === "water" || c.biome === "mountains" || c.settlement !== "none") continue;
      const touchesWater = neighbors(c.q, c.r).some(
        ([nq, nr]) => cells.get(cellKey(nq, nr))?.biome === "water",
      );
      if (touchesWater && c.biome !== "river" && rng() < 0.45) {
        if (c.biome === "plains" || c.biome === "marsh" || c.biome === "coast") {
          c.biome = "coast";
        }
      }
    }
  }

  // Pass 2 — mountains: singular peaks or short ranges
  const landKeys = [...cells.values()]
    .filter((c) => c.biome !== "water")
    .map((c) => cellKey(c.q, c.r));
  // Shuffle land for seed starts
  for (let i = landKeys.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = landKeys[i]!;
    landKeys[i] = landKeys[j]!;
    landKeys[j] = t;
  }

  const targetMountainShare = 0.07 + rng() * 0.04; // ~7–11% of land
  const mountainBudget = Math.max(3, Math.floor(landKeys.length * targetMountainShare));
  let painted = 0;
  let seedIdx = 0;

  while (painted < mountainBudget && seedIdx < landKeys.length) {
    const startKey = landKeys[seedIdx++]!;
    const start = cells.get(startKey);
    if (!start || start.biome === "water" || start.biome === "mountains") continue;

    // Singular peak (~40%) or range of length 2–5
    const singular = rng() < 0.4;
    const rangeLen = singular ? 1 : 2 + Math.floor(rng() * 4);
    start.biome = "mountains";
    painted++;

    if (rangeLen === 1) continue;

    // Walk a preferred direction to form a ridge
    const dir = AXIAL_DIRS[Math.floor(rng() * AXIAL_DIRS.length)]!;
    let cq = start.q;
    let cr = start.r;
    for (let step = 1; step < rangeLen && painted < mountainBudget; step++) {
      // Mostly follow dir; occasional side step for natural wobble
      const wobble = rng() < 0.28;
      const [dq, dr] = wobble
        ? AXIAL_DIRS[Math.floor(rng() * AXIAL_DIRS.length)]!
        : dir;
      cq += dq;
      cr += dr;
      const n = cells.get(cellKey(cq, cr));
      if (!n || n.biome === "water" || n.biome === "mountains") break;
      n.biome = "mountains";
      painted++;
    }
  }

  // Pass 3 — foothills more frequent around mountains
  for (const c of cells.values()) {
    if (c.biome === "water" || c.biome === "mountains" || c.biome === "coast") continue;
    let nearMtn = false;
    for (const [nq, nr] of neighbors(c.q, c.r)) {
      const n = cells.get(cellKey(nq, nr));
      if (n?.biome === "mountains") {
        nearMtn = true;
        break;
      }
    }
    if (!nearMtn) continue;
    const p = rng();
    // Strong bias to foothills next to peaks; leftover stays hills/forest/plains mix
    if (p < 0.72) c.biome = "foothills";
    else if (p < 0.88 && c.biome !== "marsh") c.biome = "hills";
  }

  // Also a light sprinkle of foothills not forced by adjacency (upland feel)
  for (const c of cells.values()) {
    if (c.biome !== "hills") continue;
    if (rng() < 0.12) c.biome = "foothills";
  }

  // P3.7 Pass 3b — seasonal mountain passes (summer thaw). Not settleable.
  {
    const mtns = [...cells.values()].filter((c) => c.biome === "mountains");
    const passBudget = mtns.length === 0 ? 0 : Math.max(1, Math.floor(mtns.length / 10));
    const edgeMtns = mtns.filter((c) =>
      neighbors(c.q, c.r).some(([nq, nr]) => {
        const n = cells.get(cellKey(nq, nr));
        return !!n && n.biome !== "mountains" && n.biome !== "water" && isDryLand(n.biome);
      }),
    );
    const pool = (edgeMtns.length >= passBudget ? edgeMtns : mtns).slice();
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const t = pool[i]!;
      pool[i] = pool[j]!;
      pool[j] = t;
    }
    let placed = 0;
    for (const c of pool) {
      if (placed >= passBudget) break;
      if (c.settlement !== "none") continue;
      if (c.mountainPass) continue;
      c.mountainPass = true;
      placed++;
    }
  }

  // Pass 4 — winding rivers that always terminate in water
  const waterCells = [...cells.values()].filter((c) => c.biome === "water");
  if (waterCells.length === 0) {
    // Guarantee at least one water on the rim so rivers can terminate
    const rim = [...cells.values()]
      .filter((c) => hexDistance(c.q, c.r, 0, 0) >= radius - 1)
      .sort((a, b) => a.q - b.q || a.r - b.r);
    if (rim[0]) {
      rim[0].biome = "water";
      waterCells.push(rim[0]);
    }
  }

  const riverCount = Math.max(2, Math.min(5, Math.floor(radius / 2)));
  const usedRiver = new Set<string>();

  for (let ri = 0; ri < riverCount; ri++) {
    // Source: inland dry cell, prefer hills/foothills/mountains edge
    const sources = [...cells.values()].filter((c) => {
      const d = hexDistance(c.q, c.r, 0, 0);
      return (
        isDryLand(c.biome) &&
        d <= radius - 2 &&
        d >= 1 &&
        !usedRiver.has(cellKey(c.q, c.r)) &&
        (c.biome === "mountains" ||
          c.biome === "foothills" ||
          c.biome === "hills" ||
          rng() < 0.15)
      );
    });
    if (sources.length === 0) break;
    const src = sources[Math.floor(rng() * sources.length)]!;

    // Target: nearest water (with slight rng among closest few)
    const waters = [...waterCells].sort(
      (a, b) =>
        hexDistance(src.q, src.r, a.q, a.r) - hexDistance(src.q, src.r, b.q, b.r),
    );
    const pickN = Math.min(3, waters.length);
    const target = waters[Math.floor(rng() * pickN)]!;

    // Greedy winding path toward water; never paint the water cell itself
    // P3.8: max 3 consecutive straight axial steps — force meander after that
    let cur = src;
    let guard = 0;
    let lastDq = 0;
    let lastDr = 0;
    let straightRun = 0;
    const maxSteps = Math.floor(radius * 7.5); // P3.7 +25% river length
    while (guard++ < maxSteps) {
      if (cur.biome === "water") break;
      const ck = cellKey(cur.q, cur.r);
      if (cur.settlement === "none") {
        cur.biome = "river";
        usedRiver.add(ck);
      }

      // Done when adjacent to water
      const neigh = neighbors(cur.q, cur.r)
        .map(([nq, nr]) => cells.get(cellKey(nq, nr)))
        .filter((n): n is HexCell => !!n);
      if (neigh.some((n) => n.biome === "water")) break;

      const distToTarget = hexDistance(cur.q, cur.r, target.q, target.r);
      // Score neighbors: prefer closer to water target, avoid existing river loops, mild random
      let best: HexCell | null = null;
      let bestScore = -Infinity;
      for (const n of neigh) {
        if (n.biome === "water") {
          best = n;
          bestScore = Infinity;
          break;
        }
        const nd = hexDistance(n.q, n.r, target.q, target.r);
        const progress = distToTarget - nd;
        const already = usedRiver.has(cellKey(n.q, n.r)) ? -2.5 : 0;
        const mtnPen = n.biome === "mountains" ? -0.8 : 0;
        const stepDq = n.q - cur.q;
        const stepDr = n.r - cur.r;
        const sameDir = straightRun > 0 && stepDq === lastDq && stepDr === lastDr;
        // P3.8 hard cap: ≤3 collinear river tiles ⇒ at most 2 same-dir steps in a row
        if (sameDir && straightRun >= 2) continue;
        const score =
          progress * 2.2 + already + mtnPen + (rng() - 0.5) * 1.4;
        if (score > bestScore) {
          bestScore = score;
          best = n;
        }
      }
      if (!best || cellKey(best.q, best.r) === cellKey(cur.q, cur.r)) {
        // Force a step toward any water if stuck (prefer non-straight when capped)
        let fallback: HexCell | null = null;
        let fd = Infinity;
        for (const w of waterCells) {
          for (const n of neigh) {
            if (n.biome === "water") continue;
            const stepDq = n.q - cur.q;
            const stepDr = n.r - cur.r;
            const sameDir = straightRun > 0 && stepDq === lastDq && stepDr === lastDr;
            if (sameDir && straightRun >= 2) continue;
            const d = hexDistance(n.q, n.r, w.q, w.r);
            if (d < fd) {
              fd = d;
              fallback = n;
            }
          }
        }
        if (!fallback) {
          for (const w of waterCells) {
            for (const n of neigh) {
              if (n.biome === "water") continue;
              const d = hexDistance(n.q, n.r, w.q, w.r);
              if (d < fd) {
                fd = d;
                fallback = n;
              }
            }
          }
        }
        if (!fallback) break;
        best = fallback;
      }
      const stepDq = best.q - cur.q;
      const stepDr = best.r - cur.r;
      if (stepDq === lastDq && stepDr === lastDr && straightRun > 0) {
        straightRun += 1;
      } else {
        lastDq = stepDq;
        lastDr = stepDr;
        straightRun = 1;
      }
      cur = best;
      if (cur.biome === "water") break;
    }

    // Ensure termination: if last river cell is not adjacent to water, dig toward nearest water
    const riverCells = [...usedRiver]
      .map((k) => cells.get(k))
      .filter((c): c is HexCell => !!c && c.biome === "river");
    // Check this run's end (cur) adjacency
    if (cur.biome === "river" || isDryLand(cur.biome)) {
      const adjWater = neighbors(cur.q, cur.r).some(([nq, nr]) => {
        const n = cells.get(cellKey(nq, nr));
        return n?.biome === "water";
      });
      if (!adjWater) {
        // Extend up to radius*3 steps toward nearest water (P3.8 meander ≤3 tiles)
        let extendGuard = 0;
        let extLastDq = 0;
        let extLastDr = 0;
        let extStraight = 0;
        while (extendGuard++ < radius * 3) {
          const nearest = waterCells.reduce(
            (best, w) => {
              const d = hexDistance(cur.q, cur.r, w.q, w.r);
              return d < best.d ? { w, d } : best;
            },
            { w: waterCells[0]!, d: Infinity },
          );
          if (nearest.d <= 1) break;
          let step: HexCell | null = null;
          let sd = Infinity;
          let sawWater = false;
          for (const [nq, nr] of neighbors(cur.q, cur.r)) {
            const n = cells.get(cellKey(nq, nr));
            if (!n) continue;
            if (n.biome === "water") {
              sawWater = true;
              continue;
            }
            const stepDq = nq - cur.q;
            const stepDr = nr - cur.r;
            const sameDir = extStraight > 0 && stepDq === extLastDq && stepDr === extLastDr;
            if (sameDir && extStraight >= 2) continue;
            const d = hexDistance(nq, nr, nearest.w.q, nearest.w.r);
            if (d < sd) {
              sd = d;
              step = n;
            }
          }
          if (sawWater) break;
          // If meander blocked all progress, allow one straight escape then re-cap
          if (!step) {
            for (const [nq, nr] of neighbors(cur.q, cur.r)) {
              const n = cells.get(cellKey(nq, nr));
              if (!n || n.biome === "water") continue;
              const d = hexDistance(nq, nr, nearest.w.q, nearest.w.r);
              if (d < sd) {
                sd = d;
                step = n;
              }
            }
          }
          if (!step) break;
          const stepDq = step.q - cur.q;
          const stepDr = step.r - cur.r;
          if (stepDq === extLastDq && stepDr === extLastDr && extStraight > 0) {
            extStraight += 1;
          } else {
            extLastDq = stepDq;
            extLastDr = stepDr;
            extStraight = 1;
          }
          // Carve valleys through peaks when needed so rivers can terminate
          step.biome = "river";
          usedRiver.add(cellKey(step.q, step.r));
          cur = step;
          if (
            neighbors(cur.q, cur.r).some(([nq, nr]) => cells.get(cellKey(nq, nr))?.biome === "water")
          ) {
            break;
          }
        }
      }
    }

    // Soft touch: if a river run failed to touch water, convert a rim path — last resort paint one water neighbor link
    void riverCells;
  }

  // Final river integrity: every river hex must reach water via river/water walk; else carve toward water
  // P3.8: connect ↔ hard-cap loop; ALWAYS finish on enforce so ensure can't leave long straights
  for (let riverPass = 0; riverPass < 5; riverPass++) {
    ensureRiversReachWater(cells, rng, radius);
    enforceRiverStraightCap(cells, 3);
  }
}

/**
 * P3.8: no axial-collinear river run longer than maxTiles (default 3).
 * Clears every (maxTiles+1)th tile on a collinear run so residual segments
 * are ≤ maxTiles. Settlements never cleared. Gaps are re-linked afterward by
 * ensureRiversReachWater using a meander-forced carve.
 */
function enforceRiverStraightCap(
  cells: Map<string, HexCell>,
  maxTiles = 3,
): void {
  const dirs: readonly [number, number][] = [
    [1, 0],
    [0, 1],
    [-1, 1],
    [-1, 0],
    [0, -1],
    [1, -1],
  ];
  const toClear = new Set<string>();
  for (const head of cells.values()) {
    if (head.biome !== "river") continue;
    for (const [dq, dr] of dirs) {
      if (cells.get(cellKey(head.q - dq, head.r - dr))?.biome === "river") continue;
      const run: HexCell[] = [head];
      let q = head.q + dq;
      let r = head.r + dr;
      while (true) {
        const n = cells.get(cellKey(q, r));
        if (!n || n.biome !== "river") break;
        run.push(n);
        q += dq;
        r += dr;
      }
      if (run.length <= maxTiles) continue;
      // Keep maxTiles, clear one gap, repeat: indices maxTiles, 2*maxTiles+1, ...
      for (let i = maxTiles; i < run.length; i += maxTiles + 1) {
        const cell = run[i]!;
        if (cell.settlement === "none") toClear.add(cellKey(cell.q, cell.r));
      }
    }
  }
  for (const k of toClear) {
    const c = cells.get(k);
    if (c && c.biome === "river" && c.settlement === "none") c.biome = "plains";
  }
}

function ensureRiversReachWater(
  cells: Map<string, HexCell>,
  _rng: () => number,
  radius: number,
): void {
  const waters = [...cells.values()].filter((c) => c.biome === "water");
  if (waters.length === 0) return;

  const reachesWater = (start: HexCell): boolean => {
    const seen = new Set<string>();
    const stack: HexCell[] = [start];
    while (stack.length) {
      const c = stack.pop()!;
      const k = cellKey(c.q, c.r);
      if (seen.has(k)) continue;
      seen.add(k);
      if (c.biome === "water") return true;
      for (const [nq, nr] of neighbors(c.q, c.r)) {
        const n = cells.get(cellKey(nq, nr));
        if (!n) continue;
        if (n.biome === "water" || n.biome === "river") stack.push(n);
      }
    }
    return false;
  };

  /** Side offsets for forced zig when a straight run hits the cap. */
  const sideSteps = (
    dq: number,
    dr: number,
  ): readonly [number, number][] => {
    // Neighbors that are not forward/back along travel
    const all: [number, number][] = [
      [1, 0],
      [0, 1],
      [-1, 1],
      [-1, 0],
      [0, -1],
      [1, -1],
    ];
    return all.filter(
      ([sq, sr]) =>
        !(sq === dq && sr === dr) && !(sq === -dq && sr === -dr),
    );
  };

  /**
   * Greedy carve start → nearest water with hard meander:
   * never more than 2 consecutive same-dir steps (⇒ ≤3 collinear river tiles).
   * When blocked, take a mandatory side-step then resume.
   */
  const carveMeanderToWater = (start: HexCell): void => {
    if (reachesWater(start)) return;
    let cur = start;
    let lastDq = 0;
    let lastDr = 0;
    let straight = 0;
    const maxIter = Math.max(24, Math.floor(radius * 10));
    for (let i = 0; i < maxIter; i++) {
      if (
        neighbors(cur.q, cur.r).some(
          ([nq, nr]) => cells.get(cellKey(nq, nr))?.biome === "water",
        )
      ) {
        return;
      }
      const nearest = waters.reduce(
        (best, w) => {
          const d = hexDistance(cur.q, cur.r, w.q, w.r);
          return d < best.d ? { w, d } : best;
        },
        { w: waters[0]!, d: Infinity },
      );

      const tryStep = (allowStraight: boolean): HexCell | null => {
        let best: HexCell | null = null;
        let bd = Infinity;
        for (const [nq, nr] of neighbors(cur.q, cur.r)) {
          const n = cells.get(cellKey(nq, nr));
          if (!n || n.biome === "water") continue;
          if (n.settlement !== "none") continue;
          const stepDq = nq - cur.q;
          const stepDr = nr - cur.r;
          const sameDir = straight > 0 && stepDq === lastDq && stepDr === lastDr;
          if (!allowStraight && sameDir && straight >= 2) continue;
          const d = hexDistance(nq, nr, nearest.w.q, nearest.w.r);
          // Prefer progress; slight preference for already-river
          const score = d - (n.biome === "river" ? 0.25 : 0);
          if (score < bd) {
            bd = score;
            best = n;
          }
        }
        return best;
      };

      let best = tryStep(false);
      // Mandatory side-step when capped or when no oblique progress exists
      if (!best) {
        const sides =
          straight >= 1
            ? sideSteps(lastDq, lastDr)
            : ([
                [1, 0],
                [0, 1],
                [-1, 1],
                [-1, 0],
                [0, -1],
                [1, -1],
              ] as const);
        let sideBest: HexCell | null = null;
        let sbd = Infinity;
        for (const [sq, sr] of sides) {
          const n = cells.get(cellKey(cur.q + sq, cur.r + sr));
          if (!n || n.biome === "water") continue;
          if (n.settlement !== "none") continue;
          // Never continue the same axial direction when already at cap
          if (
            straight >= 2 &&
            lastDq === sq &&
            lastDr === sr
          ) {
            continue;
          }
          const d = hexDistance(n.q, n.r, nearest.w.q, nearest.w.r);
          if (d < sbd) {
            sbd = d;
            sideBest = n;
          }
        }
        best = sideBest;
      }
      // Absolute last resort: any neighbor except continuing a capped straight
      if (!best) {
        let fb: HexCell | null = null;
        let fd = Infinity;
        for (const [nq, nr] of neighbors(cur.q, cur.r)) {
          const n = cells.get(cellKey(nq, nr));
          if (!n || n.biome === "water") continue;
          if (n.settlement !== "none") continue;
          const stepDq = nq - cur.q;
          const stepDr = nr - cur.r;
          if (straight >= 2 && stepDq === lastDq && stepDr === lastDr) continue;
          const d = hexDistance(nq, nr, nearest.w.q, nearest.w.r);
          if (d < fd) {
            fd = d;
            fb = n;
          }
        }
        best = fb;
      }
      if (!best) return;

      const stepDq = best.q - cur.q;
      const stepDr = best.r - cur.r;
      if (stepDq === lastDq && stepDr === lastDr && straight > 0) {
        straight += 1;
      } else {
        lastDq = stepDq;
        lastDr = stepDr;
        straight = 1;
      }
      if (best.settlement === "none" && best.biome !== "water") {
        best.biome = "river";
      }
      cur = best;
    }
  };

  for (let pass = 0; pass < 4; pass++) {
    let stranded = 0;
    for (const c of cells.values()) {
      if (c.biome !== "river") continue;
      if (reachesWater(c)) continue;
      stranded++;
      carveMeanderToWater(c);
    }
    if (stranded === 0) break;
  }
}

const BANNER_NAMES = [
  "Ashford",
  "Rookhaven",
  "Millstone",
  "Greywatch",
  "Thornwall",
  "Redmere",
  "Coldwell",
  "Harwick",
] as const;

/** Fixed names for the Phase 4.5 political triad (not vassal banners). */
const POLITICAL_NAMES: Record<PoliticalSeatKind, string> = {
  liege: "Highcourt",
  peer: "Fairhaven",
  foe: "Ironhold",
};

/**
 * Minimum axial hex distance between any two settlements on initial worldgen
 * (seat + bannermen). Init-only — runtime growth/spawn is parked elsewhere.
 * Small test boards clamp below this so unit maps still place a few banners.
 */
export const MIN_SETTLEMENT_DISTANCE = 6;

/** Default play disk radius (sized so ~3–5 bannermen pack at MIN_SETTLEMENT_DISTANCE). */
export const DEFAULT_MAP_RADIUS = 14;

/**
 * Effective min separation for this board: full rule when the disk can hold it,
 * otherwise best-effort clamp so tiny test radii still generate bannermen.
 */
export function settlementMinDistance(radius: number): number {
  return Math.min(MIN_SETTLEMENT_DISTANCE, Math.max(2, radius - 1));
}

/**
 * Place exactly one liege, one peer, one foe on dry land, separated from the
 * player cluster (seat + banners) and each other. Skipped on tiny test boards
 * that cannot hold three extra keeps at minSep.
 */
function placePoliticalTriad(
  cells: Map<string, HexCell>,
  rng: () => number,
  radius: number,
  _seat: HexCell,
  placed: HexCell[],
  minSep: number,
): PoliticalSeat[] {
  // Need room beyond the player/banner cluster — tiny unit maps skip triad.
  if (radius < 8) return [];

  const kinds: PoliticalSeatKind[] = ["liege", "peer", "foe"];
  const out: PoliticalSeat[] = [];
  const localPlaced = [...placed];

  const freeCandidates = (): HexCell[] => {
    const list = [...cells.values()].filter((c) => {
      if (!canSettleOn(c) || c.settlement !== "none") return false;
      return localPlaced.every(
        (p) => hexDistance(c.q, c.r, p.q, p.r) >= minSep,
      );
    });
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = list[i]!;
      list[i] = list[j]!;
      list[j] = tmp;
    }
    return list;
  };

  for (const kind of kinds) {
    const pool = freeCandidates();
    if (pool.length === 0) break;

    // Prefer keeps outside the player cluster: maximize min-distance to seat+banners.
    // Liege wants farthest; peer mid-far; foe far but not clustered on liege.
    pool.sort((a, b) => {
      const minToPlayer = (c: HexCell) =>
        Math.min(...placed.map((p) => hexDistance(c.q, c.r, p.q, p.r)));
      const da = minToPlayer(a);
      const db = minToPlayer(b);
      if (kind === "liege") return db - da || a.q - b.q || a.r - b.r;
      if (kind === "foe") {
        const liege = out.find((s) => s.kind === "liege");
        if (liege) {
          const la = hexDistance(a.q, a.r, liege.q, liege.r);
          const lb = hexDistance(b.q, b.r, liege.q, liege.r);
          // Prefer away from liege among far-from-player cells
          return db - da || lb - la || a.q - b.q;
        }
        return db - da || a.q - b.q;
      }
      // peer: far from player, not required to hug liege
      return db - da || a.q - b.q || a.r - b.r;
    });

    const c = pool[0]!;
    c.settlement = "political";
    c.seatRole = kind;
    c.name = POLITICAL_NAMES[kind];
    if (!canSettleOn(c)) {
      c.biome = "plains";
      delete c.mountainPass;
    }
    if (c.resourceFlag) delete c.resourceFlag;
    const key = cellKey(c.q, c.r);
    out.push({ key, kind, q: c.q, r: c.r, name: c.name });
    localPlaced.push(c);
  }

  // Only accept a complete triad (exactly liege + peer + foe). Partial → roll back.
  if (out.length !== 3) {
    for (const s of out) {
      const cell = cells.get(s.key);
      if (cell) {
        cell.settlement = "none";
        delete cell.seatRole;
        delete cell.name;
      }
    }
    return [];
  }
  return out;
}

/** Lookup a political seat by kind (liege | peer | foe). */
export function politicalSeatByKind(
  map: HexMap,
  kind: PoliticalSeatKind,
): PoliticalSeat | undefined {
  return map.politicalSeats.find((s) => s.kind === kind);
}

/**
 * Generate a seeded axial hex disk.
 * Seat near origin on dry land; a few bannermen on dry cells; simple routes;
 * Phase 4.5: exactly one liege + one peer + one foe when the board has room.
 * Default radius packs seat + bannermen at ≥ MIN_SETTLEMENT_DISTANCE.
 */
export function generateHexMap(seed: string, radius = DEFAULT_MAP_RADIUS): HexMap {
  const rng = createRng(seed);
  const cells = new Map<string, HexCell>();

  for (let q = -radius; q <= radius; q++) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r++) {
      cells.set(cellKey(q, r), {
        q,
        r,
        biome: "plains",
        settlement: "none",
        routes: [],
      });
    }
  }

  paintBiomes(cells, rng, radius);
  placeResourceFlags(cells, rng);

  const dry = [...cells.values()].filter((c) => canSettleOn(c));
  // Prefer seat near center
  dry.sort(
    (a, b) =>
      hexDistance(a.q, a.r, 0, 0) - hexDistance(b.q, b.r, 0, 0) ||
      a.q - b.q ||
      a.r - b.r,
  );
  const seat = dry[0] ?? [...cells.values()][0]!;
  seat.settlement = "seat";
  seat.name = "Your Seat";
  // Seat must stay dry land — if biome was river somehow, promote to plains
  if (!canSettleOn(seat)) { seat.biome = "plains"; delete seat.mountainPass; }
  // Settlements don't keep strategic deposits on the home hex
  if (seat.resourceFlag) delete seat.resourceFlag;
  const seatKey = cellKey(seat.q, seat.r);

  // Init-only: no two settlements within minSep hexes (seat + all bannermen).
  const minSep = settlementMinDistance(radius);

  // Bannermen: dry cells in the outer band, at least minSep from the seat
  const candidates = dry.filter((c) => {
    const d = hexDistance(c.q, c.r, seat.q, seat.r);
    return canSettleOn(c) && c.settlement === "none" && d >= minSep && d <= Math.max(minSep, radius - 1);
  });
  // Shuffle-ish via rng
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = candidates[i]!;
    candidates[i] = candidates[j]!;
    candidates[j] = tmp;
  }
  const bannerCount = Math.min(5, Math.max(3, Math.floor(radius / 2)));
  const bannermanKeys: string[] = [];
  const placed: HexCell[] = [seat];
  for (const c of candidates) {
    if (bannermanKeys.length >= bannerCount) break;
    const farEnough = placed.every(
      (p) => hexDistance(c.q, c.r, p.q, p.r) >= minSep,
    );
    if (!farEnough) continue;
    c.settlement = "bannerman";
    c.name = BANNER_NAMES[bannermanKeys.length % BANNER_NAMES.length]!;
    if (!canSettleOn(c)) { c.biome = "plains"; delete c.mountainPass; }
    if (c.resourceFlag) delete c.resourceFlag;
    bannermanKeys.push(cellKey(c.q, c.r));
    placed.push(c);
  }

  // Phase 4.5: liege + 1 peer + 1 foe (no 4× seat scaffold)
  const politicalSeats = placePoliticalTriad(
    cells,
    rng,
    radius,
    seat,
    placed,
    minSep,
  );
  const liegeKey =
    politicalSeats.find((s) => s.kind === "liege")?.key ?? null;

  // Routes: seat ↔ bannermen / liege along cost-aware greedy paths.
  // Favor plains/forest/coast; soft-avoid hills/marsh/foothills; never mountains/river/water.
  const addRoute = (a: HexCell, b: HexCell) => {
    const ka = cellKey(a.q, a.r);
    const kb = cellKey(b.q, b.r);
    if (!a.routes.includes(kb)) a.routes.push(kb);
    if (!b.routes.includes(ka)) b.routes.push(ka);
  };

  /** Traversal cost for road laying (lower = preferred). Infinity = impassable. */
  const roadEnterCost = (biome: Biome): number => {
    switch (biome) {
      case "plains":
      case "coast":
        return 1;
      case "forest":
        return 1.15;
      case "foothills":
        return 1.55;
      case "hills":
        return 2.1;
      case "marsh":
        return 2.4;
      case "mountains":
      case "river":
      case "water":
        return Number.POSITIVE_INFINITY;
      default:
        return 1.5;
    }
  };

  /**
   * Prefer merge-then-branch: strong bias onto cells that already carry a road
   * so new legs share corridors instead of running parallel highways.
   */
  const stepToward = (from: HexCell, to: HexCell): HexCell => {
    let best = from;
    let bestScore = Number.POSITIVE_INFINITY;
    const baseD = hexDistance(from.q, from.r, to.q, to.r);
    const fromKey = cellKey(from.q, from.r);
    for (const [nq, nr] of neighbors(from.q, from.r)) {
      const n = cells.get(cellKey(nq, nr));
      if (!n || !isDryLand(n.biome)) continue;
      // Absolute ban: mountains never carry roads (passes stay corridors, not highways)
      if (n.biome === "mountains") continue;
      const cost = roadEnterCost(n.biome);
      if (!Number.isFinite(cost)) continue;
      const d = hexDistance(nq, nr, to.q, to.r);
      // Must make progress or at least not backtrack farther than needed
      if (d > baseD) continue;
      const nk = cellKey(nq, nr);
      // Already on the network? Strong pull so later routes merge into trunks.
      const onExisting =
        n.routes.length > 0 || from.routes.includes(nk) || n.routes.includes(fromKey);
      const mergeBias = onExisting ? -4.2 : 0;
      // Soft anti-parallel: if neighbor is virgin land but an adjacent cell
      // already has a road toward the same target band, slight penalty.
      let parallelTax = 0;
      if (!onExisting) {
        for (const [pq, pr] of neighbors(nq, nr)) {
          const p = cells.get(cellKey(pq, pr));
          if (p && p.routes.length > 0 && hexDistance(pq, pr, to.q, to.r) <= d + 1) {
            parallelTax = 1.35;
            break;
          }
        }
      }
      // Weighted: distance progress primary, terrain + merge secondary
      const score = d * 10 + cost + mergeBias + parallelTax;
      if (score < bestScore) {
        bestScore = score;
        best = n;
      }
    }
    // Fallback: pure greedy dry-land step if cost filter left us stuck
    if (best === from) {
      let bestD = baseD;
      for (const [nq, nr] of neighbors(from.q, from.r)) {
        const n = cells.get(cellKey(nq, nr));
        if (!n || !isDryLand(n.biome) || n.biome === "mountains") continue;
        const d = hexDistance(nq, nr, to.q, to.r);
        if (d < bestD) {
          bestD = d;
          best = n;
        }
      }
    }
    return best;
  };

  const layRoute = (targetKey: string) => {
    const target = cells.get(targetKey);
    if (!target) return;
    let cur = seat;
    let guard = 0;
    while (cellKey(cur.q, cur.r) !== targetKey && guard++ < radius * 6) {
      const next = stepToward(cur, target);
      if (cellKey(next.q, next.r) === cellKey(cur.q, cur.r)) break;
      addRoute(cur, next);
      cur = next;
    }
  };

  // Longest legs first so the trunk forms before short spurs branch off it.
  const routeTargets = [...bannermanKeys];
  if (liegeKey) routeTargets.push(liegeKey);
  routeTargets.sort((a, b) => {
    const ca = cells.get(a);
    const cb = cells.get(b);
    if (!ca || !cb) return 0;
    return (
      hexDistance(seat.q, seat.r, cb.q, cb.r) -
      hexDistance(seat.q, seat.r, ca.q, ca.r)
    );
  });
  for (const tk of routeTargets) layRoute(tk);

  return {
    seed,
    radius,
    cells,
    seatKey,
    bannermanKeys,
    politicalSeats,
    liegeKey,
  };
}

export const BIOME_COLORS: Record<Biome, string> = {
  plains: "#6b8f4e",
  forest: "#2f5d3a",
  hills: "#8a7a4a",
  foothills: "#9a8f6a",
  mountains: "#6e6a68",
  marsh: "#4a6b5a",
  coast: "#c2b280",
  river: "#3d6f8f",
  water: "#2a4a6a",
};

export const BIOME_LABELS: Record<Biome, string> = {
  plains: "Plains",
  forest: "Forest",
  hills: "Hills",
  foothills: "Foothills",
  mountains: "Mountains",
  marsh: "Marsh",
  coast: "Coast",
  river: "River",
  water: "Water",
};
